import { createElement } from 'react'
import { TFunction } from 'i18next'
import { KnowledgeAskCitation, KnowledgeDocumentListItem } from '@digital-twins/shared-types'
import {
  AssistantCalculationEvidence,
  AssistantConnectorRecordEvidence,
  AssistantRoomEvidence,
  AssistantStudyEvidence,
  AssistantWebEvidence,
} from '@digital-twins/shared-types/assistant'

import {
  CitationFigure,
  CitationSource,
  citationDocType,
  citationVerification,
  figureIndexForClaim,
  hostnameFromUrl,
  readableQuote,
} from '@/components/citations'
import { ROUTES } from '@/routes/routes'

import {
  citationSocialPost,
  SocialCitationPreview,
  socialCitationExcerpt,
} from '../KnowledgeHubPage/social/SocialCitationPostMeta'
import { citationImage } from './citationImage'
import { isCitationUuid } from './injectCitationMarkers'
import { EvidenceCitationTarget, hasRegion } from './partitionAssistantCitations'
import { studyResultsTarget } from './studyCitationTarget'
import { formatPublishedDate } from './ThreadWebSourceCard'

/**
 * Page-level adapters from this page's citation shapes to the shared
 * `CitationSource` (components/citations). The shared module never sees a
 * `KnowledgeAskCitation` or an evidence item; the social preview is injected
 * here so the module never imports page components.
 */
export interface CitationSourceContext {
  t: TFunction<'app'>
  resolveLabel: (citation: KnowledgeAskCitation) => string
  docsByToken?: Map<string, KnowledgeDocumentListItem>
  onOpenCitation: (index: number, citation: KnowledgeAskCitation) => void
  onOpenCalculation?: (evidenceId: string) => void
  /** Study and room sources open an in-app route (study results, chat room). */
  onNavigate?: (path: string) => void
}

const CALCULATION_SOURCE_LABEL_KEYS = {
  study: 'threads.detail.calculation.sourceStudy',
  run: 'threads.detail.calculation.sourceRun',
  audience: 'threads.detail.calculation.sourceAudience',
  dataset: 'threads.detail.calculation.sourceDataset',
} as const

/** Calculation fields added after the generated types (D4/D5); read defensively. */
interface CalculationDisplayFields {
  sourceName?: string | null
  figures?: Array<CitationFigure> | null
}

function cleanText(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null
}

function httpsRecordUrl(value: string | null | undefined): string | null {
  const trimmed = cleanText(value)
  if (!trimmed) {
    return null
  }
  try {
    return new URL(trimmed).protocol === 'https:' ? trimmed : null
  } catch {
    return null
  }
}

function validFigures(raw: unknown): Array<CitationFigure> | null {
  if (!Array.isArray(raw)) {
    return null
  }
  const figures: Array<CitationFigure> = []
  for (const entry of raw) {
    const figure = entry as Partial<CitationFigure> | null
    if (!figure || typeof figure.label !== 'string' || typeof figure.unit !== 'string' ||
        typeof figure.value !== 'number' || !Number.isFinite(figure.value)) {
      // All or nothing: compacting would shift every later cited position.
      return null
    }
    figures.push({ label: figure.label, value: figure.value, unit: figure.unit })
  }
  return figures
}

/** "Q: option" → "option" when the card's location already names the exhibit "Q". */
function withoutTitlePrefix(figure: CitationFigure, title: string | null): CitationFigure {
  if (!title) {
    return figure
  }
  const prefix = `${title}: `
  if (figure.label.startsWith(prefix) && figure.label.length > prefix.length) {
    return { ...figure, label: figure.label.slice(prefix.length) }
  }
  return figure
}

/**
 * The card's exhibit title. A title that is just the first figure's full
 * label is the older fallback ("How often: Daily"); it reads as the question
 * head only, so a sentence citing another figure never shows "Daily".
 */
function calculationTitle(evidence: AssistantCalculationEvidence): string | null {
  const title = cleanText(evidence.title)
  if (!title) {
    return null
  }
  const raw = (evidence as AssistantCalculationEvidence & CalculationDisplayFields).figures
  const firstLabel = Array.isArray(raw) ? cleanText((raw[0] as Partial<CitationFigure> | null)?.label) : null
  const separator = title.indexOf(': ')
  if (firstLabel === title && separator > 0) {
    return title.slice(0, separator).trim() || null
  }
  return title
}

/**
 * The cited figure only. With no cited figure (or an out-of-range one), all
 * figures when there are 3 or fewer, else none: a 5-figure table with no
 * figure named shows its title and base only, never a guessed number.
 */
export function calculationFiguresForCard(
  evidence: AssistantCalculationEvidence,
  figure: number | null | undefined,
  claim?: string | null,
): Array<CitationFigure> | null {
  const figures = validFigures((evidence as AssistantCalculationEvidence & CalculationDisplayFields).figures)
  if (!figures || figures.length === 0) {
    return null
  }
  const title = calculationTitle(evidence)
  if (figure != null && Number.isInteger(figure) && figure >= 0 && figure < figures.length) {
    return [withoutTitlePrefix(figures[figure], title)]
  }
  // A `[[E<n>]]` reference names no figure: the percentage the sentence states does.
  const stated = figureIndexForClaim(figures, claim)
  if (stated != null) {
    return [withoutTitlePrefix(figures[stated], title)]
  }
  if (figures.length <= 3) {
    return figures.map((entry) => withoutTitlePrefix(entry, title))
  }
  return null
}

function calculationKind(evidence: AssistantCalculationEvidence): keyof typeof CALCULATION_SOURCE_LABEL_KEYS {
  const kind = evidence.source?.kind
  return kind === 'run' || kind === 'audience' || kind === 'dataset' ? kind : 'study'
}

function webSource(citation: AssistantWebEvidence, index: number, claim: string | null): CitationSource {
  const hostname = hostnameFromUrl(citation.url)
  const publisher = cleanText(citation.publisher)
  const name = cleanText(citation.title) ?? publisher ?? hostname ?? citation.url
  const date = formatPublishedDate(citation.publishedAt ?? undefined)
  const location = [hostname ?? publisher, date].filter((part): part is string => Boolean(part)).join(' · ')
  return {
    key: `web:${citation.url}`,
    displayIndex: index,
    kind: 'web',
    name,
    shortName: hostname ?? publisher ?? name,
    hostname,
    location: location || null,
    // Only text the source page provided; an OpenAI web hit has none.
    quote: readableQuote(citation.snippet, claim),
    verification: 'matched',
    onOpen: () => {
      window.open(citation.url, '_blank', 'noopener,noreferrer')
    },
  }
}

/** A study finding: the study name as the pill, the finding headline as the location. */
function studySource(
  evidence: AssistantStudyEvidence,
  index: number,
  ctx: CitationSourceContext,
  claim: string | null,
): CitationSource {
  // Never the token as a name: an untitled study reads as the generic label.
  const name = cleanText(evidence.title) ?? ctx.t('threads.detail.calculation.sourceStudy')
  return {
    key: `study:${evidence.id}`,
    displayIndex: index,
    kind: 'study',
    goalId: evidence.goalId,
    name,
    shortName: name,
    location: cleanText(evidence.location),
    quote: readableQuote(evidence.snippet, claim),
    verification: 'matched',
    onOpen: () => {
      let path: string
      try {
        path = studyResultsTarget(evidence.studyToken)
      } catch {
        return
      }
      ctx.onNavigate?.(path)
    },
  }
}

/** A connected record: the title as the pill, the app as the location, Open when the url is https. */
function connectorSource(
  evidence: AssistantConnectorRecordEvidence,
  index: number,
  claim: string | null,
): CitationSource {
  const title = cleanText(evidence.title) ?? evidence.app.trim()
  const href = httpsRecordUrl(evidence.url)
  return {
    key: `connector:${evidence.id}`,
    displayIndex: index,
    kind: 'connector_record',
    name: title,
    shortName: title,
    location: evidence.app.trim(),
    quote: readableQuote(evidence.excerpt, claim),
    verification: 'matched',
    href,
    onOpen: () => {
      if (href) {
        window.open(href, '_blank', 'noopener,noreferrer')
      }
    },
  }
}

/** A twin's line in a room: the room title as the pill, the speaker as the location. */
function roomSource(
  evidence: AssistantRoomEvidence,
  index: number,
  ctx: CitationSourceContext,
  claim: string | null,
): CitationSource {
  const name = cleanText(evidence.title) ?? ctx.t('notifications.untitledChat')
  return {
    key: `room:${evidence.id}`,
    displayIndex: index,
    kind: 'room',
    name,
    shortName: name,
    location: cleanText(evidence.speaker),
    quote: readableQuote(evidence.snippet, claim),
    verification: 'matched',
    onOpen: () => {
      if (isCitationUuid(evidence.roomToken)) {
        ctx.onNavigate?.(ROUTES.chatRoom(evidence.roomToken.trim()))
      }
    },
  }
}

function calculationSource(
  evidence: AssistantCalculationEvidence,
  index: number,
  ctx: CitationSourceContext,
  figure: number | null | undefined,
  claim: string | null,
): CitationSource {
  const kind = calculationKind(evidence)
  const kindLabel = ctx.t(CALCULATION_SOURCE_LABEL_KEYS[kind])
  const sourceName = cleanText((evidence as AssistantCalculationEvidence & CalculationDisplayFields).sourceName)
  const title = calculationTitle(evidence)
  const base = ctx.t('threads.detail.calculation.base', { count: evidence.base })
  return {
    key: `calculation:${evidence.id}`,
    displayIndex: index,
    kind,
    name: sourceName ?? kindLabel,
    shortName: sourceName ?? kindLabel,
    location: [title, base].filter((part): part is string => Boolean(part)).join(' · '),
    figures: calculationFiguresForCard(evidence, figure, claim),
    verification: 'matched',
    onOpen: () => ctx.onOpenCalculation?.(evidence.id),
  }
}

function documentLocation(citation: KnowledgeAskCitation, t: TFunction<'app'>): string | null {
  const timestamp = cleanText(citation.timestampLabel)
  if (timestamp) {
    return timestamp
  }
  if (!citation.pageUnknown && citation.pageNumber != null) {
    return t('threads.detail.sourcePill.page', { page: citation.pageNumber })
  }
  return null
}

function documentSource(
  citation: KnowledgeAskCitation,
  index: number,
  ctx: CitationSourceContext,
  evidence: { title?: string | null; snippet?: string | null } | null,
  claim: string | null,
): CitationSource {
  const label = ctx.resolveLabel(citation)
  const socialPost = citationSocialPost(citation)
  if (socialPost) {
    const handle = socialPost.authorHandle ? `@${socialPost.authorHandle}` : null
    // V2 quotes the resolved evidence passage; legacy quotes the post itself.
    const excerpt = evidence ? cleanText(evidence.snippet) ?? '' : socialCitationExcerpt(citation)
    return {
      key: `social:${citation.citationToken ?? citation.documentToken ?? index}:${index}`,
      displayIndex: index,
      kind: 'social',
      name: cleanText(evidence?.title) ?? label,
      shortName: handle ?? label,
      platform: socialPost.platform,
      location: null,
      preview: createElement(SocialCitationPreview, {
        post: socialPost,
        excerpt,
        testId: `thread-citation-social-preview-${index}`,
      }),
      verification: citationVerification(citation),
      onOpen: () => ctx.onOpenCitation(index, citation),
    }
  }
  const quote = readableQuote(evidence ? evidence.snippet : citation.snippet, claim)
  const doc = citation.documentToken ? ctx.docsByToken?.get(citation.documentToken) : undefined
  const isStudy = citation.granularity === 'study'
  return {
    key: `document:${citation.citationToken ?? citation.documentToken ?? label}:${index}`,
    displayIndex: index,
    kind: isStudy ? 'study' : 'library',
    name: cleanText(evidence?.title) ?? label,
    shortName: label,
    docType: isStudy ? null : citationDocType(doc),
    location: isStudy ? null : documentLocation(citation, ctx.t),
    quote,
    verification: documentVerification(citation),
    onOpen: () => ctx.onOpenCitation(index, withQuotedPassage(citation, quote)),
  }
}

/**
 * The source view quotes, locates and highlights the passage the card showed,
 * so the two never disagree. The stored character offsets addressed the raw
 * snippet, so they go with it.
 */
function withQuotedPassage(citation: KnowledgeAskCitation, quote: string | null): KnowledgeAskCitation {
  if (!quote || quote === citation.snippet) {
    return citation
  }
  return { ...citation, snippet: quote, unitCharStart: null, unitCharEnd: null }
}

/** A drawn page region is exact, so it never carries the "approximate" hint. */
function documentVerification(citation: KnowledgeAskCitation): CitationSource['verification'] {
  const verification = citationVerification(citation)
  return verification === 'page_only' && hasRegion(citation) ? 'matched' : verification
}

/**
 * A v2 evidence target as a source. `index` is the 1-based target index (the
 * number the href carries); `figure` is the cited figure position, if any.
 */
export function citationSourceForTarget(
  target: EvidenceCitationTarget,
  index: number,
  ctx: CitationSourceContext,
  figure?: number | null,
  claim?: string | null,
): CitationSource {
  return { ...citationSourceBodyForTarget(target, index, ctx, figure, claim), image: citationImage(target, ctx) }
}

function citationSourceBodyForTarget(
  target: EvidenceCitationTarget,
  index: number,
  ctx: CitationSourceContext,
  figure?: number | null,
  claim?: string | null,
): CitationSource {
  if (target.kind === 'web') {
    return webSource(target.citation, index, claim ?? null)
  }
  if (target.kind === 'calculation') {
    return calculationSource(target.evidence, index, ctx, figure, claim ?? null)
  }
  if (target.kind === 'study') {
    return studySource(target.evidence, index, ctx, claim ?? null)
  }
  if (target.kind === 'room') {
    return roomSource(target.evidence, index, ctx, claim ?? null)
  }
  if (target.kind === 'connector_record') {
    return connectorSource(target.evidence, index, claim ?? null)
  }
  // page_region opens like a library citation; its region rides on the citation.
  return documentSource(target.citation, index, ctx, target.evidence, claim ?? null)
}

/** A legacy (non-v2) document citation as a source. */
export function citationSourceForLegacy(
  citation: KnowledgeAskCitation,
  index: number,
  ctx: CitationSourceContext,
  claim?: string | null,
): CitationSource {
  return documentSource(citation, index, ctx, null, claim ?? null)
}
